import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FiX, FiCornerUpRight, FiSearch, FiUser, FiInfo } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { workflowsAPI, usersAPI } from '../../services/api';
import { localizeSeeded } from '../../pages/FlowManagement/flowUtils';
import './WorkflowActionModals.scss';

// Yönlendir — the action owner hands their own active workflow action to
// another user. The step and the run stay unchanged; only the owner does.
// Reason is mandatory.
function ForwardActionModal({ open, onClose, approval, onDone }) {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQuery(''); setResults([]); setTarget(null); setReason('');
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); return undefined; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await usersAPI.search(q, 8);
        setResults(r.data.users || []);
      } catch { setResults([]); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, open]);

  const handleSubmit = async () => {
    if (!target) return toast.error(t('workflowActions.forward.targetRequired'));
    if (!reason.trim()) return toast.error(t('workflowActions.forward.reasonRequired'));
    setSubmitting(true);
    try {
      await workflowsAPI.forward(approval._id, { toUserId: target._id, reason: reason.trim() });
      toast.success(t('workflowActions.forward.success', { name: `${target.firstName} ${target.lastName}` }));
      onClose();
      onDone?.();
    } catch (err) {
      const code = err.response?.data?.code;
      const codeMsg = {
        SAME_USER: t('workflowActions.forward.errSameUser'),
        COMPANY_MISMATCH: t('workflowActions.forward.errCompany'),
        TARGET_INACTIVE: t('workflowActions.forward.errInactive'),
        FORWARD_NOT_ALLOWED: t('workflowActions.forward.errNotAllowed'),
        STEP_NOT_ACTIVE: t('workflowActions.errStepNotActive'),
      }[code];
      toast.error(codeMsg || err.response?.data?.error || t('workflowActions.forward.failed'));
    }
    setSubmitting(false);
  };

  if (!open) return null;

  return (
    <div className="wfam-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="wfam" onClick={e => e.stopPropagation()}>
        <header className="wfam__header">
          <div className="wfam__title">
            <span className="wfam__title-icon wfam__title-icon--forward"><FiCornerUpRight size={16} /></span>
            <div>
              <h3>{t('workflowActions.forward.title')}</h3>
              <p>{localizeSeeded(approval?.stepName, i18n.language)}</p>
            </div>
          </div>
          <button className="wfam__close" onClick={onClose} aria-label={t('common.cancel')}><FiX size={16} /></button>
        </header>

        <div className="wfam__body">
          <div className="wfam__field">
            <label>{t('workflowActions.forward.targetLabel')} <span className="wfam__req">*</span></label>
            {target ? (
              <div className="wfam__picked">
                <span className="wfam__avatar">{(target.firstName?.[0] || '') + (target.lastName?.[0] || '')}</span>
                <span className="wfam__picked-info">
                  <strong>{target.firstName} {target.lastName}</strong>
                  <em>{target.email}</em>
                </span>
                <button type="button" className="wfam__picked-clear" onClick={() => setTarget(null)} aria-label={t('common.cancel')}>
                  <FiX size={14} />
                </button>
              </div>
            ) : (
              <div className="wfam__search">
                <FiSearch className="wfam__search-icon" />
                <input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={t('workflowActions.searchUserPlaceholder')}
                  autoFocus
                  // An in-app people search — the browser's contact/address
                  // autofill must stay out of it.
                  name="wfam-user-search"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                />
                {(results.length > 0 || searching) && (
                  <div className="wfam__results">
                    {searching && <div className="wfam__results-note">{t('common.loading')}</div>}
                    {results.map(u => (
                      <button key={u._id} type="button" className="wfam__result" onClick={() => { setTarget(u); setQuery(''); setResults([]); }}>
                        <span className="wfam__avatar"><FiUser size={12} /></span>
                        <span className="wfam__result-info">
                          <strong>{u.firstName} {u.lastName}</strong>
                          <em>{u.email}</em>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="wfam__field">
            <label>{t('workflowActions.forward.reasonLabel')} <span className="wfam__req">*</span></label>
            <textarea
              className="wfam__textarea"
              rows={3}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder={t('workflowActions.forward.reasonPlaceholder')}
              maxLength={1000}
            />
          </div>

          <div className="wfam__note">
            <FiInfo size={13} />
            <span>{t('workflowActions.forward.hint')}</span>
          </div>
        </div>

        <footer className="wfam__footer">
          <button className="wfam__btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="wfam__btn wfam__btn--forward" onClick={handleSubmit} disabled={submitting}>
            <FiCornerUpRight size={14} />
            {submitting ? t('workflowActions.submitting') : t('workflowActions.forward.confirm')}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default ForwardActionModal;
